'use client';

// FAQ · cabeçalho no padrão Integrações + accordion com animação framer-motion
// (height auto na descida) e single-open (abrir um fecha os outros). "Fale com a
// gente" abre o chat widget (evento trakd:open-chat). Aprovado no companion (2026-06-17).

import { AnimatePresence, motion } from 'motion/react';
import { useState, type ReactNode } from 'react';
import { TbCheck } from 'react-icons/tb';

function Checklist({ items }: { items: string[] }) {
  return (
    <ul className="my-3 grid gap-2">
      {items.map((t) => (
        <li key={t} className="flex items-center gap-2.5 text-[14px] font-medium text-[#cfc9de]">
          <span className="grid h-[18px] w-[18px] flex-none place-items-center rounded-md bg-[rgba(91,227,167,0.15)] text-[#5be3a7]">
            <TbCheck size={12} strokeWidth={3} />
          </span>
          {t}
        </li>
      ))}
    </ul>
  );
}

const FAQS: { q: string; a: ReactNode }[] = [
  {
    q: 'Funciona com qualquer site?',
    a: (
      <>
        Sim. Você instala com <b>IA usando sua IDE favorita</b> (Claude Code, VS Code, Cursor,
        Antigravity e mais) ou, se usa <b>WordPress</b>, com um snippet simples no site. Funciona em
        qualquer página.
      </>
    ),
  },
  {
    q: 'Por que com a Trakd não preciso de GTM (Google Tag Manager)?',
    a: (
      <>
        Porque a Trakd já faz o trabalho do GTM, <b>direto no servidor</b>.
        <br />
        <br />
        Muita gente usa GTM porque o Pixel sozinho perde dados com iOS, bloqueadores e navegadores
        privados. Aí precisa montar container, trigger, tag, dataLayer e ainda um servidor separado
        pra tentar recuperar os eventos.
        <br />
        <br />
        Na Trakd você instala <b>1 linha na página</b> e conecta <b>1 webhook</b> da sua plataforma
        de pagamento. O resto já vem pronto:
        <Checklist
          items={[
            'CAPI server-side',
            'fbp/fbc automático',
            'deduplicação de eventos',
            'atribuição cross-session',
            'recuperação de eventos',
            'tracking mais estável',
          ]}
        />
        Sem container, sem trigger, sem dataLayer : sem configuração técnica complicada.
      </>
    ),
  },
  {
    q: 'Por que meu pixel não aparece no Pixel Meta Helper (Auxiliar de Pixel da Meta)?',
    a: (
      <>
        Porque o tracking é <b>server-side</b> : e isso é uma <b>vantagem</b>. O Pixel Helper (e
        qualquer curioso ou concorrente) só consegue ver o pixel <b>do navegador</b> (o <b>fbq</b>
        /fbevents.js). Como a Trakd manda os eventos direto do <b>servidor</b> pra Meta, via CAPI,
        os seus <b>dados de evento ficam escondidos</b>: ninguém bisbilhota o seu pixel, o seu
        volume de vendas ou a sua estrutura de tracking abrindo o Pixel Helper no seu site.
        <br />
        <br />
        Ou seja: não aparecer ali é o comportamento esperado : e ainda <b>
          protege os seus dados
        </b>{' '}
        de gente mal-intencionada.
        <br />
        <br />
        Se o Helper mostrar outro pixel, é o <b>pixel próprio do site</b> (instalado à parte,
        client-side). Pra conferir os eventos da Trakd, abra o <b>Gerenciador de Eventos da Meta</b>
        , selecione o seu pixel e veja os eventos com origem <b>Servidor</b> (ou use o{' '}
        <b>Testar eventos</b>): é lá que o PageView e a compra aparecem.
      </>
    ),
  },
  {
    q: 'Qual diferença da Trakd para a UTMify?',
    a: (
      <>
        A UTMify é uma ferramenta de <b>UTM</b>: ela salva os parâmetros de origem das suas visitas.
        A Trakd vai muito além : é um <b>tracking server-side</b> que dispara os eventos de compra
        direto pro servidor da Meta.
        <br />
        <br />
        Ou seja: quem de verdade precisa conhecer o seu comprador : a <b>Meta</b> : recebe o dado
        completo e confiável pra otimizar suas campanhas, mesmo com iOS, bloqueadores e cookies
        caindo.
      </>
    ),
  },
  {
    q: 'Funciona em qualquer plataforma de pagamento?',
    a: (
      <>
        Hoje temos <b>integrações nativas</b>: Hotmart, Kiwify, Cakto, Eduzz, Kirvano, Hubla,
        Perfect Pay, Lastlink, Ticto, Lowify, GGCheckout, Stripe, Shopify, WooCommerce, Yampi,
        Greenn, Cartpanda, Braip, Pepper, Monetizze, Systeme.io, Digital Manager Guru e Vega
        Checkout. Não achou a sua? É só pedir no botão <b>“Sugerir plataforma”</b> na seção de
        integrações que a gente avalia.
      </>
    ),
  },
  {
    q: 'Preciso de programador para instalar?',
    a: (
      <>
        Não. A instalação é <b>guiada por IA</b>: você roda dentro da sua IDE (Claude Code, VS Code,
        Cursor, Antigravity) e ela faz o trabalho por você. Quem usa WordPress só cola um snippet. E
        se travar, o suporte te ajuda.
      </>
    ),
  },
  {
    q: 'Quando começo a ver os resultados?',
    a: (
      <>
        Rápido. Em <b>3 a 7 dias</b> a qualidade dos seus eventos de <b>Purchase</b> e{' '}
        <b>InitiateCheckout</b> já costuma chegar em <b>9.1</b> na Qualidade de Correspondência do
        Gerenciador de Eventos da Meta : que é justamente o que faz a entrega das suas campanhas
        melhorar.
      </>
    ),
  },
  {
    q: 'Quanto custa pra começar?',
    a: (
      <>
        O plano inicial é <b>R$ 97/mês</b> (Solo), com 150 mil eventos/mês : mais que suficiente pra
        quem está começando. E já vem completo: <b>CAPI server-side</b>, público de reembolso
        automático, <b>relatórios diários</b>, <b>MCP pra usar com IA</b> e as integrações nativas.
        Conforme você cresce, sobe de plano e ganha mais contas Meta, pixels e públicos.
      </>
    ),
  },
  {
    q: 'Tem taxa por venda, limite ou percentual sobre faturamento?',
    a: (
      <>
        Não. Nada de taxa por venda, comissão sobre o seu faturamento ou limite escondido. Você paga
        só a <b>mensalidade fixa</b> do seu plano : o que vender é todo seu.
      </>
    ),
  },
  {
    q: 'Posso cancelar a qualquer momento?',
    a: (
      <>
        Sim. Sem fidelidade, sem multa. A cobrança ocorre no ato e você tem{' '}
        <b>7 dias de garantia de reembolso integral</b> (CDC, art. 49): cancele dentro do prazo e o
        valor volta automaticamente. Depois, é mensalidade recorrente e você cancela quando quiser,{' '}
        <b>direto no painel</b>.
      </>
    ),
  },
  {
    q: 'Tracking server-side é seguro pra LGPD?',
    a: (
      <>
        É, sim. Antes de mandar qualquer coisa pra Meta, a Trakd <b>embaralha os dados pessoais</b>{' '}
        (e-mail, telefone, CPF) de um jeito que não dá pra desfazer : a Meta só recebe o necessário
        pra reconhecer a venda, nunca os dados crus. Tem banner de consentimento pronto e, se alguém
        pedir, você apaga os dados daquela pessoa num clique.
      </>
    ),
  },
  {
    q: 'O que são esses limites de eventos?',
    a: (
      <>
        Cada ação que a Trakd rastreia no seu site e envia pra Meta conta como <b>1 evento</b>. O
        limite do plano é a <b>soma de todos os eventos no mês</b> : não é só venda, toda interação
        rastreada entra na conta. Por exemplo:
        <Checklist
          items={[
            'Visita de página (PageView)',
            'Início de checkout (InitiateCheckout)',
            'Compra (Purchase)',
            'Cancelamento de assinatura (CancelSubscription)',
          ]}
        />
        Os planos vão de <b>150 mil/mês</b> (Solo) a <b>5 milhões/mês</b> (Agência). Se apertar, é
        só subir de plano : a gente te avisa antes de estourar, nada para de funcionar de surpresa.
      </>
    ),
  },
];

function openChat(): void {
  window.dispatchEvent(new CustomEvent('trakd:open-chat'));
}

export function FaqAccordion() {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <section className="mx-auto max-w-3xl px-6 py-20 md:py-28">
      <div className="text-center">
        <p className="text-trakd-brand mb-3 text-xs font-semibold tracking-[0.22em] uppercase">
          FAQ
        </p>
        <h2 className="text-3xl font-semibold tracking-tight md:text-5xl">
          Perguntas <span className="text-trakd-brand">frequentes</span>
        </h2>
        <p className="text-ink-muted mx-auto mt-4 max-w-xl text-base md:text-lg">
          Tudo que você precisa saber antes de começar a trackear.
        </p>
      </div>

      <div className="mt-12 space-y-3">
        {FAQS.map((qa, i) => {
          const isOpen = open === i;
          return (
            <div
              key={qa.q}
              className={`overflow-hidden rounded-[14px] border transition-colors ${
                isOpen ? 'border-violet/40 bg-violet/5' : 'border-violet/20 bg-white/[0.02]'
              }`}
            >
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : i)}
                aria-expanded={isOpen}
                className="text-ink flex w-full cursor-pointer items-center justify-between gap-4 px-5 py-[18px] text-left text-[15px] font-medium md:text-base"
              >
                <span>{qa.q}</span>
                <span
                  className={`grid h-[26px] w-[26px] flex-none place-items-center rounded-full transition-[transform,background] duration-300 ${
                    isOpen ? 'bg-trakd-fill rotate-45 text-white' : 'bg-violet/15 text-[#c2a9ff]'
                  }`}
                  aria-hidden
                >
                  <svg viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5">
                    <path d="M8 1.5a.75.75 0 0 1 .75.75v5h5a.75.75 0 0 1 0 1.5h-5v5a.75.75 0 0 1-1.5 0v-5h-5a.75.75 0 0 1 0-1.5h5v-5A.75.75 0 0 1 8 1.5z" />
                  </svg>
                </span>
              </button>
              <AnimatePresence initial={false}>
                {isOpen ? (
                  <motion.div
                    key="content"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="text-ink-muted px-5 pb-5 text-[14.5px] leading-relaxed [&_b]:font-semibold [&_b]:text-[#d8d2ea]">
                      {qa.a}
                    </div>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      <p className="text-ink-faint mt-10 text-center text-sm">
        Ficou com outra dúvida?{' '}
        <button
          type="button"
          onClick={openChat}
          className="text-violet underline underline-offset-[3px] transition hover:text-[#c2a9ff]"
        >
          Fale com a gente
        </button>
        .
      </p>
    </section>
  );
}
